import React, { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Snackbar, TextInput as PaperTextInput, useTheme } from 'react-native-paper';
import { DatePickerModal } from 'react-native-paper-dates';
import BaseBottomSheet from './BaseBottomSheet';
import TruckDropdown from './TruckDropdown';
import { useAuth } from '../context/AuthContext';
import JobService from '../services/jobService';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';

interface RecordCompletedJobBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RecordCompletedJobBottomSheet: React.FC<RecordCompletedJobBottomSheetProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [customerPhone, setCustomerPhone] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [serviceCost, setServiceCost] = useState('');
  const [serviceDate, setServiceDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [cargoTags, setCargoTags] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [paymentType, setPaymentType] = useState<'cash' | 'debt' | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState('');

  // Only the money figures and the truck/payment are required; the trip details
  // (phone, from, to, tags) are optional.
  const isValid =
    amount.trim().length > 0 &&
    serviceCost.trim().length > 0 &&
    selectedTruck !== null &&
    paymentType !== null;

  const resetForm = useCallback(() => {
    setCustomerPhone('');
    setFrom('');
    setTo('');
    setAmount('');
    setServiceCost('');
    setServiceDate(new Date());
    setCargoTags('');
    setDescription('');
    setSelectedTruck(null);
    setPaymentType(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!isValid || !user?.uid || !selectedTruck || !paymentType) {
      return;
    }

    try {
      setSaving(true);
      const intakeAmount = parseFloat(amount);
      const cost = parseFloat(serviceCost);
      await JobService.createJob(user.uid, {
        customerPhone: customerPhone.trim(),
        from: from.trim(),
        to: to.trim(),
        intakeAmount,
        serviceCost: cost,
        // Revenue is recognized at completion: net the intake price against cost.
        amount: intakeAmount - cost,
        cargoTags: cargoTags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
        description: description.trim(),
        status: 'completed',
        assignedTruckId: selectedTruck.id,
        paymentType,
        completedBy: user.uid,
        completedAt: serviceDate,
        ...(user.displayName
          ? { createdByName: user.displayName, completedByName: user.displayName }
          : {}),
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      ErrorLogger.logError(error as any, 'RecordCompletedJobBottomSheet.handleSubmit');
      setErrorSnackbar('Failed to record job. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    isValid,
    user,
    selectedTruck,
    paymentType,
    customerPhone,
    from,
    to,
    amount,
    serviceCost,
    serviceDate,
    cargoTags,
    description,
    resetForm,
    onSuccess,
    onClose,
  ]);

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <>
      <BaseBottomSheet
        visible={visible}
        onClose={handleClose}
        title="Record Job"
        height={0.85}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Negotiated Amount */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Negotiated Amount *"
              value={amount}
              onChangeText={setAmount}
              keyboardType="numeric"
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-amount"
            />
          </View>

          {/* Service Cost */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Service Cost *"
              value={serviceCost}
              onChangeText={setServiceCost}
              keyboardType="numeric"
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-service-cost"
            />
          </View>

          {/* Truck Selector — required, grouped with the other required fields */}
          <View style={styles.fieldContainer}>
            <TruckDropdown
              selectedTruck={selectedTruck}
              onTruckSelect={setSelectedTruck}
              label="Truck"
              placeholder="Select a truck"
              required={true}
            />
          </View>

          {/* Payment Type — required */}
          <View style={[styles.fieldContainer, styles.paymentRow]}>
            <Button
              mode={paymentType === 'cash' ? 'contained' : 'outlined'}
              onPress={() => setPaymentType('cash')}
              style={styles.paymentButton}
              testID="payment-cash"
            >
              Cash
            </Button>
            <Button
              mode={paymentType === 'debt' ? 'contained' : 'outlined'}
              onPress={() => setPaymentType('debt')}
              style={styles.paymentButton}
              testID="payment-debt"
            >
              Debt
            </Button>
          </View>

          {/* Service Date */}
          <View style={styles.fieldContainer}>
            <Button
              mode="text"
              onPress={() => setShowDatePicker(true)}
              icon="calendar"
              style={[styles.dateButton, { backgroundColor: theme.colors.surfaceVariant }]}
              contentStyle={styles.dateButtonContent}
              testID="input-service-date"
            >
              {`Service Date: ${formatDate(serviceDate)}`}
            </Button>
          </View>

          {/* Customer Phone */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Customer Phone (Optional)"
              value={customerPhone}
              onChangeText={setCustomerPhone}
              keyboardType="phone-pad"
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-phone"
            />
          </View>

          {/* From */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="From (Optional)"
              value={from}
              onChangeText={setFrom}
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-from"
            />
          </View>

          {/* To */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="To (Optional)"
              value={to}
              onChangeText={setTo}
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-to"
            />
          </View>

          {/* Cargo Tags */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Cargo Tags (Optional, comma-separated)"
              value={cargoTags}
              onChangeText={setCargoTags}
              placeholder="e.g. sand, gravel, timber"
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-tags"
            />
          </View>

          {/* Description */}
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Description (Optional)"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
              style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
              testID="input-description"
            />
          </View>
        </ScrollView>

        {/* Submit Button */}
        <View style={styles.buttonContainer}>
          <Button
            mode="contained"
            onPress={handleSubmit}
            disabled={!isValid || saving}
            loading={saving}
            testID="submit-job-button"
          >
            {saving ? 'Recording...' : 'Record Job'}
          </Button>
        </View>
      </BaseBottomSheet>

      <DatePickerModal
        locale="en-GB"
        mode="single"
        visible={showDatePicker}
        onDismiss={() => setShowDatePicker(false)}
        date={serviceDate}
        onConfirm={(params) => {
          setShowDatePicker(false);
          if (params.date) {
            setServiceDate(params.date);
          }
        }}
      />

      <Snackbar
        visible={errorSnackbar.length > 0}
        onDismiss={() => setErrorSnackbar('')}
        duration={4000}
        action={{ label: 'OK', onPress: () => setErrorSnackbar('') }}
      >
        {errorSnackbar}
      </Snackbar>
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 20,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  input: {},
  dateButton: {
    width: '100%',
    borderRadius: 4,
  },
  dateButtonContent: {
    justifyContent: 'flex-start',
    height: 48,
  },
  paymentRow: {
    flexDirection: 'row',
    gap: 12,
  },
  paymentButton: {
    flex: 1,
  },
  buttonContainer: {
    padding: 16,
    paddingBottom: 16,
  },
});

export default RecordCompletedJobBottomSheet;
